"use client";

import { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, Info, Plus, Trash2, Upload } from "lucide-react";
import { api, type Agent, type CredentialSlot, type KnowledgeConnectorBinding } from "@/lib/api";
import { alternateAgentAvatarUrl } from "@/lib/agent-media";
import { normalizeAgentMode, type AgentMode } from "@/lib/agent-mode";
import { AgentMarkdownEditor } from "@/components/agent-markdown-editor";
import { Switch } from "@/components/ui/switch";

/** LiveAvatar Context prompt hard store cap (API silently truncates above). */
export const LIVEAVATAR_PROMPT_MAX = 65535;
const LINK_FAQ_MAX = 240;
const LINKS_MAX = 20;
const CONNECTORS_MAX = 8;
const HTTP_TOOLS_MAX = 16;

export type CascadeConversationFieldId =
  | "greetingText"
  | "systemPrompt"
  | "knowledgeBase"
  | "liveSources";

export type LiveAvatarConversationFieldId =
  | "greetingText"
  | "liveAvatarPrompt"
  | "liveAvatarLinks";

type CascadeTab = {
  id: CascadeConversationFieldId;
  label: string;
  tip: string;
  max?: number;
  labelEditor?: string;
};

type LiveAvatarTextTab = {
  id: "greetingText" | "liveAvatarPrompt";
  label: string;
  tip: string;
  max: number;
  labelEditor: string;
};

const CASCADE_TABS: CascadeTab[] = [
  {
    id: "greetingText",
    label: "Greeting",
    tip: "Spoken once when the user connects. Keep it short, warm, and on-brand.",
    max: 500,
    labelEditor: "Greeting line",
  },
  {
    id: "systemPrompt",
    label: "System prompt",
    tip: "Defines the agent's core reply behavior, persona, style, and response length. This prompt shapes how the agent responds across the entire conversation.",
    max: 4000,
    labelEditor: "Instructions",
  },
  {
    id: "knowledgeBase",
    label: "Knowledge base",
    tip: "Curated facts appended under “Knowledge base:” in the system message. Prefer short, verifiable bullets. For live lookups, use Live sources.",
    max: 4000,
    labelEditor: "Knowledge",
  },
  {
    id: "liveSources",
    label: "Live sources",
    tip: "Optional connectors that expose lookup tools to the Direct brain at call time. Markdown Knowledge base stays static; these bindings call an HTTPS JSON API. Cascade saves bindings but does not run connector tools yet.",
  },
];

function normalizeConnectors(raw: Agent["knowledgeConnectors"]): KnowledgeConnectorBinding[] {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, CONNECTORS_MAX).map((row, idx) => {
    const tools = Array.isArray(row?.config?.tools)
      ? row.config!.tools!.slice(0, HTTP_TOOLS_MAX).map((t) => ({
          name: String(t?.name || "").trim(),
          description: String(t?.description || "").trim(),
          method: (String(t?.method || "GET").toUpperCase() === "POST" ? "POST" : "GET") as
            | "GET"
            | "POST",
          path: String(t?.path || "/").trim() || "/",
          parameters: t?.parameters || { type: "object", properties: {}, required: [] },
        }))
      : [];
    return {
      id: String(row?.id || `source_${idx + 1}`).trim(),
      type: String(row?.type || "http_api").trim() || "http_api",
      enabled: row?.enabled !== false,
      credentialSlotId: row?.credentialSlotId ? String(row.credentialSlotId) : null,
      config: {
        baseUrl: String(row?.config?.baseUrl || "").trim(),
        timeoutMs: Number(row?.config?.timeoutMs) || 4000,
        tools,
      },
    };
  });
}

function emptyConnector(): KnowledgeConnectorBinding {
  return {
    id: "",
    type: "http_api",
    enabled: true,
    credentialSlotId: null,
    config: {
      baseUrl: "",
      timeoutMs: 4000,
      tools: [
        {
          name: "lookup",
          description: "Look up a fact from the live source",
          method: "GET",
          path: "/lookup",
          parameters: {
            type: "object",
            properties: { q: { type: "string", description: "Query" } },
            required: ["q"],
          },
        },
      ],
    },
  };
}

const LIVEAVATAR_TEXT_TABS: LiveAvatarTextTab[] = [
  {
    id: "greetingText",
    label: "Greeting",
    tip: "Synced to LiveAvatar Context opening_text (spoken once at session start). {firstName} is stripped on sync.",
    max: 500,
    labelEditor: "Greeting line",
  },
  {
    id: "liveAvatarPrompt",
    label: "Prompt",
    tip: "Synced to LiveAvatar Context prompt — persona, rules, and knowledge in one field (LiveAvatar has no separate system vs knowledge). Pulse prepends a spoken-length policy on Save (default 3 short sentences; more when the user asks for details). Hard limit 65,535 characters.",
    max: LIVEAVATAR_PROMPT_MAX,
    labelEditor: "Prompt",
  },
];

function CoverImg({
  src,
  avatarKey,
}: {
  src: string;
  avatarKey?: string | null;
}) {
  return (
    <img
      src={src}
      alt=""
      onError={(e) => {
        const el = e.currentTarget;
        if (el.dataset.fallbackTried) return;
        const next = alternateAgentAvatarUrl(el.src, avatarKey);
        if (!next) return;
        el.dataset.fallbackTried = "1";
        el.src = next;
      }}
    />
  );
}

type LinkRow = { url: string; faq: string; id?: string };

function normalizeLinks(raw: Agent["liveAvatarLinks"]): LinkRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r) => ({
      url: String(r?.url || "").trim(),
      faq: String(r?.faq || "").trim(),
      id: r?.id ? String(r.id).trim() : undefined,
    }))
    .filter((r) => r.url || r.faq)
    .slice(0, LINKS_MAX);
}

type Props = {
  draft: Agent;
  onChange: (next: Agent) => void;
  saving: boolean;
  onUploadCover: (file: File) => void;
};

export function AgentEditConversationPanel({
  draft,
  onChange,
  saving,
  onUploadCover,
}: Props) {
  const mode: AgentMode = normalizeAgentMode(draft.agentMode);
  const isLiveAvatar = mode === "liveavatar";
  const isDirect = mode === "direct";
  const [cascadeField, setCascadeField] =
    useState<CascadeConversationFieldId>("systemPrompt");
  const [laField, setLaField] = useState<LiveAvatarConversationFieldId>("liveAvatarPrompt");
  const [slots, setSlots] = useState<CredentialSlot[]>([]);
  const [toolsDrafts, setToolsDrafts] = useState<Record<number, string>>({});
  const fileRef = useRef<HTMLInputElement | null>(null);

  const cover = draft.avatarUrl || null;
  const links = normalizeLinks(draft.liveAvatarLinks);
  const connectors = normalizeConnectors(draft.knowledgeConnectors);

  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then((s) => {
        if (!cancelled) setSlots(Array.isArray(s.credentialSlots) ? s.credentialSlots : []);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function patch(partial: Partial<Agent>) {
    onChange({ ...draft, ...partial });
  }

  function setLinks(next: LinkRow[]) {
    patch({ liveAvatarLinks: next.slice(0, LINKS_MAX) });
  }

  function setConnectors(next: KnowledgeConnectorBinding[]) {
    patch({ knowledgeConnectors: next.slice(0, CONNECTORS_MAX) });
  }

  const cascadeTab = CASCADE_TABS.find((t) => t.id === cascadeField) || CASCADE_TABS[1];
  const laTextTab = LIVEAVATAR_TEXT_TABS.find((t) => t.id === laField);

  return (
    <div className="agent-conv">
      <section className="agent-conv-block">
        <h3 className="agent-conv-h">Identity</h3>
        <div className="agent-conv-identity">
          <div className="agent-conv-cover-col">
            {cover ? (
              <div className="agent-conv-cover">
                <CoverImg src={cover} avatarKey={draft.avatarKey} />
                <button
                  type="button"
                  className="agent-conv-cover-btn"
                  disabled={saving}
                  onClick={() => fileRef.current?.click()}
                >
                  <Camera size={14} strokeWidth={2} aria-hidden />
                  Replace cover
                </button>
              </div>
            ) : (
              <div className="agent-conv-cover-empty">
                <ImagePlus size={28} strokeWidth={1.5} aria-hidden />
                <b>No cover image yet</b>
                <button
                  type="button"
                  className="agent-edit-btn agent-edit-btn--primary agent-conv-upload"
                  disabled={saving}
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload size={14} strokeWidth={2} aria-hidden />
                  Upload image
                </button>
              </div>
            )}
            <p className="agent-conv-help">
              {cover
                ? "Uploaded image (16:9). Uploads immediately."
                : "PNG, JPG or WebP · Recommended 16:9"}
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              tabIndex={-1}
              aria-hidden
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
              disabled={saving}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) onUploadCover(file);
              }}
            />
          </div>

          <label className="agent-conv-role">
            <span>Role / Primary purpose</span>
            <input
              className="agent-conv-input"
              value={draft.role || ""}
              placeholder="Patient onboarding"
              onChange={(e) => patch({ role: e.target.value })}
            />
            <em>Shown in agent library and used for context across the workspace.</em>
          </label>
        </div>
      </section>

      <section className="agent-conv-block">
        <h3 className="agent-conv-h">Conversation</h3>
        {isLiveAvatar ? (
          <>
            <div className="agent-conv-tabs" role="tablist" aria-label="Conversation fields">
              {LIVEAVATAR_TEXT_TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={laField === t.id}
                  className={laField === t.id ? "is-active" : undefined}
                  onClick={() => setLaField(t.id)}
                >
                  {t.label}
                </button>
              ))}
              <button
                type="button"
                role="tab"
                aria-selected={laField === "liveAvatarLinks"}
                className={laField === "liveAvatarLinks" ? "is-active" : undefined}
                onClick={() => setLaField("liveAvatarLinks")}
              >
                Links{links.length ? ` (${links.length})` : ""}
              </button>
            </div>

            {laField === "liveAvatarLinks" ? (
              <>
                <div className="agent-conv-tip" role="note">
                  <span className="agent-conv-tip-icon" aria-hidden>
                    <Info size={14} />
                  </span>
                  <p>
                    Optional reference URLs synced to LiveAvatar Context links. LiveAvatar
                    fetches each page and attaches an AI summary so the avatar can ground
                    answers without stuffing the full page into Prompt.
                  </p>
                </div>
                <div className="agent-conv-links">
                  {links.length === 0 ? (
                    <p className="agent-conv-links-empty">No links yet.</p>
                  ) : (
                    links.map((row, idx) => (
                      <div key={`${row.url}-${idx}`} className="agent-conv-link-row">
                        <label>
                          <span>URL</span>
                          <input
                            className="agent-conv-input"
                            type="url"
                            value={row.url}
                            placeholder="https://example.com/docs"
                            disabled={saving}
                            onChange={(e) => {
                              const next = links.slice();
                              next[idx] = { ...row, url: e.target.value };
                              setLinks(next);
                            }}
                          />
                        </label>
                        <label>
                          <span>Label</span>
                          <input
                            className="agent-conv-input"
                            value={row.faq}
                            maxLength={LINK_FAQ_MAX}
                            placeholder="Patient hub FAQ"
                            disabled={saving}
                            onChange={(e) => {
                              const next = links.slice();
                              next[idx] = { ...row, faq: e.target.value };
                              setLinks(next);
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          className="agent-conv-link-remove"
                          disabled={saving}
                          aria-label="Remove link"
                          onClick={() => setLinks(links.filter((_, i) => i !== idx))}
                        >
                          <Trash2 size={14} strokeWidth={2} aria-hidden />
                        </button>
                      </div>
                    ))
                  )}
                  <button
                    type="button"
                    className="agent-edit-btn agent-conv-link-add"
                    disabled={saving || links.length >= LINKS_MAX}
                    onClick={() => setLinks([...links, { url: "", faq: "" }])}
                  >
                    <Plus size={14} strokeWidth={2} aria-hidden />
                    Add link
                  </button>
                </div>
              </>
            ) : laTextTab ? (
              <>
                <div className="agent-conv-tip" role="note">
                  <span className="agent-conv-tip-icon" aria-hidden>
                    <Info size={14} />
                  </span>
                  <p>{laTextTab.tip}</p>
                </div>
                <AgentMarkdownEditor
                  value={String(draft[laTextTab.id] || "")}
                  onChange={(next) => patch({ [laTextTab.id]: next } as Partial<Agent>)}
                  max={laTextTab.max}
                  label={laTextTab.labelEditor}
                  spellCheck={laTextTab.id !== "liveAvatarPrompt"}
                  emptyHint={
                    laTextTab.id === "liveAvatarPrompt"
                      ? "If empty on Save, Pulse falls back to legacy System + Knowledge until you migrate."
                      : "If left empty, we'll use a short default greeting."
                  }
                />
              </>
            ) : null}
          </>
        ) : (
          <>
            <div className="agent-conv-tabs" role="tablist" aria-label="Conversation fields">
              {CASCADE_TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={cascadeField === t.id}
                  className={cascadeField === t.id ? "is-active" : undefined}
                  onClick={() => setCascadeField(t.id)}
                >
                  {t.label}
                  {t.id === "liveSources" && connectors.length
                    ? ` (${connectors.length})`
                    : ""}
                </button>
              ))}
            </div>

            <div className="agent-conv-tip" role="note">
              <span className="agent-conv-tip-icon" aria-hidden>
                <Info size={14} />
              </span>
              <p>{cascadeTab.tip}</p>
            </div>

            {cascadeField === "liveSources" ? (
              <div className="agent-conv-connectors">
                {isDirect ? (
                  <div className="agent-perc-live-row" style={{ marginBottom: 16 }}>
                    <div>
                      <div className="agent-perc-signal-title">
                        <b>Live insight cards</b>
                      </div>
                      <span>
                        When on, successful Live sources lookups publish a metric card under the
                        Conversation user PiP, and the agent speaks a short qualitative summary
                        instead of dumping long number lists. Direct only.
                      </span>
                    </div>
                    <Switch
                      checked={draft.liveInsightCardsEnabled === true}
                      onCheckedChange={(v) => patch({ liveInsightCardsEnabled: v })}
                      aria-label="Enable live insight cards for Direct sessions"
                    />
                  </div>
                ) : null}
                {connectors.length === 0 ? (
                  <p className="agent-conv-links-empty">No live sources yet.</p>
                ) : (
                  connectors.map((row, idx) => (
                    <div key={`${row.id || "c"}-${idx}`} className="agent-conv-connector">
                      <div className="agent-conv-connector-head">
                        <label>
                          <span>Id</span>
                          <input
                            className="agent-conv-input"
                            value={row.id}
                            placeholder="fleet_kb"
                            disabled={saving}
                            onChange={(e) => {
                              const next = connectors.slice();
                              next[idx] = { ...row, id: e.target.value };
                              setConnectors(next);
                            }}
                          />
                        </label>
                        <label>
                          <span>Type</span>
                          <select
                            className="agent-conv-input"
                            value={row.type || "http_api"}
                            disabled={saving}
                            onChange={(e) => {
                              const next = connectors.slice();
                              next[idx] = { ...row, type: e.target.value };
                              setConnectors(next);
                            }}
                          >
                            <option value="http_api">http_api</option>
                          </select>
                        </label>
                        <label className="agent-conv-connector-enable">
                          <input
                            type="checkbox"
                            checked={row.enabled !== false}
                            disabled={saving}
                            onChange={(e) => {
                              const next = connectors.slice();
                              next[idx] = { ...row, enabled: e.target.checked };
                              setConnectors(next);
                            }}
                          />
                          <span>Enabled</span>
                        </label>
                        <button
                          type="button"
                          className="agent-conv-link-remove"
                          disabled={saving}
                          aria-label="Remove connector"
                          onClick={() =>
                            setConnectors(connectors.filter((_, i) => i !== idx))
                          }
                        >
                          <Trash2 size={14} strokeWidth={2} aria-hidden />
                        </button>
                      </div>
                      <div className="agent-conv-connector-grid">
                        <label>
                          <span>Base URL (https)</span>
                          <input
                            className="agent-conv-input"
                            type="url"
                            value={row.config?.baseUrl || ""}
                            placeholder="https://api.example.com/v1"
                            disabled={saving}
                            onChange={(e) => {
                              const next = connectors.slice();
                              next[idx] = {
                                ...row,
                                config: { ...row.config, baseUrl: e.target.value },
                              };
                              setConnectors(next);
                            }}
                          />
                        </label>
                        <label>
                          <span>Timeout (ms)</span>
                          <input
                            className="agent-conv-input"
                            type="number"
                            min={1}
                            max={8000}
                            value={row.config?.timeoutMs ?? 4000}
                            disabled={saving}
                            onChange={(e) => {
                              const next = connectors.slice();
                              next[idx] = {
                                ...row,
                                config: {
                                  ...row.config,
                                  timeoutMs: Number(e.target.value) || 4000,
                                },
                              };
                              setConnectors(next);
                            }}
                          />
                        </label>
                        <label>
                          <span>Credential slot</span>
                          <select
                            className="agent-conv-input"
                            value={row.credentialSlotId || ""}
                            disabled={saving}
                            onChange={(e) => {
                              const next = connectors.slice();
                              next[idx] = {
                                ...row,
                                credentialSlotId: e.target.value || null,
                              };
                              setConnectors(next);
                            }}
                          >
                            <option value="">None</option>
                            {slots.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.label || s.id}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <label className="agent-conv-connector-tools">
                        <span>Tools JSON</span>
                        <textarea
                          className="agent-conv-tools-json"
                          rows={8}
                          disabled={saving}
                          spellCheck={false}
                          value={
                            toolsDrafts[idx] ??
                            JSON.stringify(row.config?.tools || [], null, 2)
                          }
                          onChange={(e) => {
                            const text = e.target.value;
                            setToolsDrafts((prev) => ({ ...prev, [idx]: text }));
                            try {
                              const parsed = JSON.parse(text);
                              if (!Array.isArray(parsed)) return;
                              const next = connectors.slice();
                              next[idx] = {
                                ...row,
                                config: { ...row.config, tools: parsed },
                              };
                              setConnectors(next);
                            } catch {
                              /* allow incomplete JSON while typing */
                            }
                          }}
                          onBlur={() => {
                            setToolsDrafts((prev) => {
                              const copy = { ...prev };
                              delete copy[idx];
                              return copy;
                            });
                          }}
                        />
                        <em>
                          Array of tools: name, description, method (GET|POST), path, parameters
                          (JSON Schema). Direct tool names become {"{id}_{name}"}.
                        </em>
                      </label>
                    </div>
                  ))
                )}
                <button
                  type="button"
                  className="agent-edit-btn agent-conv-link-add"
                  disabled={saving || connectors.length >= CONNECTORS_MAX}
                  onClick={() => setConnectors([...connectors, emptyConnector()])}
                >
                  <Plus size={14} strokeWidth={2} aria-hidden />
                  Add live source
                </button>
              </div>
            ) : (
              <AgentMarkdownEditor
                value={String(draft[cascadeField as "greetingText" | "systemPrompt" | "knowledgeBase"] || "")}
                onChange={(next) => patch({ [cascadeField]: next } as Partial<Agent>)}
                max={cascadeTab.max || 4000}
                label={cascadeTab.labelEditor || "Text"}
                spellCheck={cascadeField !== "knowledgeBase"}
                emptyHint="If left empty, we'll use the default prompt from the selected persona template."
              />
            )}
          </>
        )}
      </section>
    </div>
  );
}
